const QUALIFIED_CANDIDATE_STATUSES = new Set([
  "ready",
  "queued",
  "processing",
  "review",
  "sending",
  "sent",
  "resolved"
]);

const ACTIVE_CANDIDATE_STATUSES = new Set([
  "ready",
  "queued",
  "processing",
  "review",
  "sending"
]);

const ACTIVE_JOB_STATUSES = new Set([
  "queued",
  "processing",
  "review",
  "sending",
  "resolving"
]);

function candidateIdentity(candidate, index = 0) {
  if (candidate?.sessionId) return `conversation:${candidate.sessionId}`;
  if (candidate?.customerKey) return `customer:${candidate.customerKey}`;
  return `candidate:${candidate?.id || index}`;
}

function jobIdentity(job, index = 0) {
  if (job?.conversationId) return `conversation:${job.conversationId}`;
  if (job?.customerKey) return `customer:${job.customerKey}`;
  return `job:${job?.id || index}`;
}

function candidateTimestamp(candidate) {
  const parsed = Date.parse(candidate?.updatedAt || candidate?.hydratedAt || candidate?.lastActivityAt || candidate?.discoveredAt || "");
  return Number.isFinite(parsed) ? parsed : 0;
}

function displayPriority(candidate) {
  const status = candidate?.status || "unknown";
  if (ACTIVE_CANDIDATE_STATUSES.has(status) || status === "sent") return 0;
  if (["blocked", "cancelled", "failed", "live"].includes(status)) return 1;
  if (status === "pending") return 2;
  return 3;
}

export function candidateStatusCounts(candidates = []) {
  return Object.fromEntries(Object.entries(candidates.reduce((counts, candidate) => {
    const status = candidate?.status || "unknown";
    counts[status] = (counts[status] || 0) + 1;
    return counts;
  }, {})).sort(([left], [right]) => left.localeCompare(right)));
}

/**
 * Keep the durable candidate ledger complete while returning only a small,
 * useful dashboard window. In-flight and recently classified conversations
 * take priority over the much larger pending backlog.
 */
export function selectDashboardCandidates(candidates = [], { limit = 250 } = {}) {
  const maximum = Math.max(1, Number(limit) || 250);
  return [...candidates]
    .sort((left, right) => displayPriority(left) - displayPriority(right)
      || candidateTimestamp(right) - candidateTimestamp(left)
      || String(left?.id || "").localeCompare(String(right?.id || "")))
    .slice(0, maximum);
}

/**
 * Derive internally consistent run totals from the complete candidate ledger
 * and full durable job audit. Jobs are evidence that a conversation qualified
 * even if an older release evicted its candidate row or later cancelled the
 * delivery during a fresh pre-send check.
 */
export function summarizeOutreachProgress({ analysis, candidates = [], jobs = [] } = {}) {
  if (!analysis?.id) return null;
  const allowRepeatPrevious = analysis.allowRepeatPrevious === true;
  const runJobs = jobs.filter((job) => job.analysisRunId === analysis.id);
  const discoveredIds = new Set();
  const pendingIds = new Set();
  const qualifiedIds = new Set();
  const excludedIds = new Set();
  const alreadyContactedIds = new Set();
  const alreadyQueuedIds = new Set();
  const outstandingIds = new Set();

  candidates.forEach((candidate, index) => {
    const identity = candidateIdentity(candidate, index);
    const status = candidate?.status || "unknown";
    discoveredIds.add(identity);
    const reason = String(candidate?.reason || "");
    if (!allowRepeatPrevious && /prior outreach already sent|already has a sent follow-up/i.test(reason)) alreadyContactedIds.add(identity);
    else if (!allowRepeatPrevious && /prior review follow-up already exists|already has a queued follow-up|already has a queued/i.test(reason)) alreadyQueuedIds.add(identity);
    else if (status === "pending") pendingIds.add(identity);
    else if (QUALIFIED_CANDIDATE_STATUSES.has(status)) qualifiedIds.add(identity);
    else excludedIds.add(identity);
    if (ACTIVE_CANDIDATE_STATUSES.has(status)) outstandingIds.add(identity);
  });

  runJobs.forEach((job, index) => {
    const identity = jobIdentity(job, index);
    discoveredIds.add(identity);
    qualifiedIds.add(identity);
    pendingIds.delete(identity);
    excludedIds.delete(identity);
    alreadyContactedIds.delete(identity);
    alreadyQueuedIds.delete(identity);
    qualifiedIds.add(identity);
    if (ACTIVE_JOB_STATUSES.has(job.status) || (job.status === "sent" && job.resolutionPending)) outstandingIds.add(identity);
  });

  const generatedJobs = runJobs.filter((job) => Boolean(job.messageDraft)).length;
  const resolved = runJobs.filter((job) => job.status === "resolved").length;
  const sentJobs = runJobs.filter((job) => Boolean(job.sentAt)).length;
  const failed = runJobs.filter((job) => job.status === "failed" || job.status === "resolution_failed").length;
  const cancelled = runJobs.filter((job) => job.status === "cancelled").length;
  const blocked = runJobs.filter((job) => job.status === "blocked").length;
  const preparedFailed = runJobs.filter((job) => Boolean(job.messageDraft) && (job.status === "failed" || job.status === "resolution_failed")).length;
  const preparedCancelled = runJobs.filter((job) => Boolean(job.messageDraft) && job.status === "cancelled").length;
  const preparedBlocked = runJobs.filter((job) => Boolean(job.messageDraft) && job.status === "blocked").length;
  const generated = Math.max(generatedJobs, resolved, sentJobs);
  const sent = Math.max(sentJobs, resolved);
  // Count every durable run job as a qualification decision. The numeric
  // lower bounds keep legacy duplicate/missing-candidate data truthful.
  const alreadyContacted = alreadyContactedIds.size;
  const alreadyQueued = alreadyQueuedIds.size;
  const qualified = Math.max(qualifiedIds.size, runJobs.length, generated, sent, resolved);
  const excluded = excludedIds.size;
  const analyzed = qualified + excluded + alreadyContacted + alreadyQueued;
  const discovered = Math.max(discoveredIds.size, analyzed);
  const pending = Math.max(0, discovered - analyzed);
  const remaining = pending + [...outstandingIds].filter((identity) => !pendingIds.has(identity)).length;

  return {
    discovered,
    analyzed,
    qualified,
    excluded,
    alreadyContacted,
    alreadyQueued,
    pending,
    generated,
    sent,
    resolved,
    failed,
    cancelled,
    blocked,
    preparedFailed,
    preparedCancelled,
    preparedBlocked,
    remaining
  };
}

/**
 * Delivery progress is intentionally separate from terminal audit progress.
 * A blocked/failed/cancelled draft is settled for queue-liveness purposes but
 * was not delivered and must never make the UI claim 100% delivery.
 */
export function summarizeDeliveryProgress(progress = {}) {
  const prepared = Math.max(0, Number(progress.generated || 0));
  const delivered = Math.min(prepared, Math.max(0, Number(progress.resolved || 0)));
  const blocked = Math.min(Math.max(0, prepared - delivered), Math.max(0, Number(progress.preparedBlocked || 0)));
  const failed = Math.min(Math.max(0, prepared - delivered - blocked), Math.max(0, Number(progress.preparedFailed || 0)));
  const cancelled = Math.min(Math.max(0, prepared - delivered - blocked - failed), Math.max(0, Number(progress.preparedCancelled || 0)));
  const awaiting = Math.max(0, prepared - delivered - blocked - failed - cancelled);
  const deliveredPercent = prepared ? Math.min(100, Math.round((delivered / prepared) * 100)) : 0;
  return { prepared, delivered, blocked, failed, cancelled, awaiting, deliveredPercent };
}

export function shouldCompleteOutreachAnalysis(analysis, progress = analysis) {
  return Boolean(
    analysis?.discoveryComplete
    && ["running", "waiting_for_budget"].includes(analysis?.status)
    && Number(progress?.pending || 0) === 0
    && Number(progress?.remaining || 0) === 0
  );
}
